import type { NextFn } from '@warp-drive/core/request';
import type { RequestContext } from '@warp-drive/core/types/request';

/**
 * Returns true while an admin-impersonated session is active. Reading at
 * request time (not construction time) lets the same handler instance see
 * impersonation flip on/off without rebuilding the RequestManager.
 */
type IsImpersonated = () => boolean;

/**
 * Resolves to `true` when the impersonating admin confirms the pending
 * mutation, `false` if they cancel. The handler aborts on `false`.
 */
type ConfirmMutation = () => Promise<boolean>;

export interface ImpersonationHandlerOptions {
  isImpersonated: IsImpersonated;
  confirmMutation: ConfirmMutation;
}

const MUTATING_METHODS = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

/**
 * Pre-request gate that mirrors `ApplicationAdapter#ajax`: during an
 * impersonated session, every mutating request waits on a confirmation
 * dialog. A "cancel" raises an AbortError so callers see the same
 * rejection shape the adapter produced.
 */
export class TioImpersonationHandler {
  constructor(private options: ImpersonationHandlerOptions) {}

  async request<T>(context: RequestContext, next: NextFn<T>) {
    const { request } = context;
    const method = (request.method ?? 'GET').toUpperCase();

    if (MUTATING_METHODS.has(method) && this.options.isImpersonated()) {
      const confirmed = await this.options.confirmMutation();
      if (!confirmed) {
        throw new DOMException(
          'Request cancelled: mutating action blocked during impersonated session',
          'AbortError'
        );
      }
    }

    return next(request);
  }
}
